import environment from '../../../common/environment';
import { PaymentOrderDetails, PaymentOrderItemData } from '../../order-confirmation/order-completion.interface';
import {
  GO_COMPANION_LAUNCH_URL,
  SHARPEN_ADVANTAGE_LAUNCH_URL,
  SHARPEN_COMPANION_LAUNCH_URL, SHARPEN_NATIONAL_LAUNCH_URL, SHARPEN_TUNNEL_LAUNCH_URL,
} from '../../upsell/constants/sharpen-page-type.constants';
import { UserOrderDetails } from '../../order-history/order-history.interface';
import { html, TemplateResult } from 'lit';
import { isMhGoItem } from '../../../common/utils/order-util';
import { isConnectProduct, isSharpenPlus } from '../../../common/utils/product-util';
import { isTunnelPurchaseFlow } from '../../../common/utils/util';

export function getSharpenVerbiage(looseLeafAvailable: boolean | undefined): TemplateResult {
  return html`
    <div class="rounded-1 mb-2">
      <h3 class="fw-bold fs-6 p-0 m-0">What's included:</h3>
      <ul id="product-info-verbiage-content" data-automation-id="product-info-verbiage-content" class="fs-small">
        <li>eBook access</li>
        <li>Online homework and quizzes</li>
        <li>Free ReadAnywhere® app to access your eBook and Smartbook assignments anywhere</li>
        <li>
          The AI-powered Sharpen exam prep app has hundreds of practice exams, flashcards, videos, and more - available on mobile and
          web.
        </li>
        ${looseLeafAvailable ? html`<li>Exclusive discounts on a loose-leaf copy of the textbook</li>`: ``}
      </ul>
    </div>
  `;
}

export function getAleksSharpenVerbiage(): TemplateResult {
  return html`
    <div class="rounded-1 mb-2">
      <h3 class="fw-bold fs-6 p-0 m-0">What's included:</h3>
      <ul id="product-info-verbiage-content" data-automation-id="product-info-verbiage-content">
        <li>
          AI-powered adaptive learning that identifies knowledge gaps and guides progress, instructor-designed homework, practice, and assessments, and access to course resources within the ALEKS platform
        </li>
        <li>
          Sharpen exam prep app with practice exams, flashcards, videos, an AI study buddy and more—available on mobile and web
        </li>
      </ul>
    </div>
  `;
}

/**
 * Checks if the order is MH GO + sharpen companion.
 *
 * @param item
 */
export function isMhGoSharpenOrder(item: PaymentOrderItemData | UserOrderDetails) {
  return isMhGoItem(item) && item?.isSharpenCompanion;
}

/**
 * Checks if the order is a sharpen bundle order (sharpen + connect or sharpen companion)
 *
 * @param userOrderDetails
 */
export function isSharpenBundleOrder(userOrderDetails: UserOrderDetails): boolean {
  return !!(userOrderDetails.isConnectSharpenBundle || userOrderDetails.isSharpenCompanion || userOrderDetails.isSharpenAdvantage);
}

/**
 * Returns the launch URL for Sharpen bundle orders.
 *
 * @param userOrderDetails
 */
export function getSharpenBundleLaunchURL(userOrderDetails: UserOrderDetails): string {
  if (userOrderDetails.isConnectSharpenBundle) {
    return environment().sharpenHomePage;
  }

  if (userOrderDetails.isSharpenCompanion) {
    return SHARPEN_COMPANION_LAUNCH_URL;
  }

  if (userOrderDetails.isSharpenAdvantage) {
    return SHARPEN_ADVANTAGE_LAUNCH_URL;
  }

  return '';
}

/**
 * Returns the item title for Sharpen companion orders.
 *
 * @param item
 */
export function getSharpenCompanionTitle(item: any): string {
  if (!item) {
    return '';
  }

  if (!(item?.isSharpenCompanion || item?.isSharpenAdvantage)) {
    return '';
  }

  if (isMhGoItem(item)) {
    return ' (GO with Sharpen Companion)';
  }

  return item.isSharpenCompanion ? ' (Connect with Sharpen Companion)' : ' (Connect with Sharpen)';
}


/*
 * Determines if a order contains sharpen plus upsell
 * along with a connect purchase
 */
export function isSharpenUpsellPresentInOrder(userOrderDetailsList: UserOrderDetails[]) {
  let hasSharpenPlusInOrder = false;
  let hasConnectInOrder = false;
  userOrderDetailsList.forEach((orderItem) => {
    if (isSharpenPlus(orderItem.programSourceId)) {
      hasSharpenPlusInOrder = true;
    } else if (isConnectProduct(orderItem)) {
      hasConnectInOrder = true;
    }
  });
  return hasSharpenPlusInOrder && hasConnectInOrder;
}

/**
 * Determines if a payment order includes a Sharpen Plus upsell alongside a Connect item.
 * @param paymentOrderDetails Payment order details to inspect.
 */
export function isSharpenUpsellPresentInPaymentOrderDetails(paymentOrderDetails: PaymentOrderDetails) {
  let hasSharpenPlusInOrder = false;
  let hasConnectInOrder = false;
  paymentOrderDetails.orderItems?.forEach((orderItem) => {
    if (isSharpenPlus(orderItem.productMetaData?.programSourceId)) {
      hasSharpenPlusInOrder = true;
    } else if (isConnectProduct(orderItem)) {
      hasConnectInOrder = true;
    }
  });
  return hasSharpenPlusInOrder && hasConnectInOrder;
}

/**
 * Returns the Sharpen launch URL based on the products
 * present in the user order and the active purchase flow.
 *
 * @param userOrderDetailsList List of user order items
 * @returns Resolved Sharpen launch URL
 */
export function getLaunchSharpenUrl(userOrderDetailsList: UserOrderDetails[]) {
  let hasSharpenPlus = false;
  let hasConnect = false;
  let hasSharpenCompanion = false;
  let hasSharpenAdvantage = false;
  let hasMhGo = false;

  userOrderDetailsList.forEach((orderItem) => {
    if (isSharpenPlus(orderItem.programSourceId)) {
      hasSharpenPlus = true;
      return;
    }
    if (isConnectProduct(orderItem)) {
      hasConnect = true;
    }
    if (isMhGoSharpenOrder(orderItem)) {
      hasMhGo = true;
    } else if (orderItem.isSharpenCompanion) {
      hasSharpenCompanion = true;
    } else if (orderItem.isSharpenAdvantage) {
      hasSharpenAdvantage = true;
    }
  });

  if (hasSharpenPlus) {
    return isTunnelPurchaseFlow() && hasConnect ? SHARPEN_TUNNEL_LAUNCH_URL : SHARPEN_NATIONAL_LAUNCH_URL;
  } else if (hasMhGo) {
    return GO_COMPANION_LAUNCH_URL;
  } else if (hasSharpenCompanion) {
    return SHARPEN_COMPANION_LAUNCH_URL;
  } else if (hasSharpenAdvantage) {
    return SHARPEN_ADVANTAGE_LAUNCH_URL;
  } else {
    return environment().sharpenHomePage;
  }
}
